import { useMemo, useState, type KeyboardEvent } from 'react';
import type { CellState, Coord, PuzzleDefinition } from '../types';
import { isGiven } from '../game/engine';

const regionColors = ['#ffd7dc','#bff1e6','#ffe5a9','#ded4fa','#d7ecff','#ffd9bd','#d7f3c5','#f2d3ee','#d8e1ff'];

export function Board({ puzzle, cells, onCell, conflictCells = [], markerAsset, patterns }: {
  puzzle: PuzzleDefinition; cells: CellState[][]; onCell: (row: number, col: number) => void;
  conflictCells?: Coord[]; markerAsset: string; patterns: boolean;
}) {
  const [focus, setFocus] = useState(0);
  const conflicts = useMemo(() => new Set(conflictCells.map(({ row, col }) => `${row}:${col}`)), [conflictCells]);
  const moveFocus = (index: number) => {
    const bounded = Math.max(0, Math.min(puzzle.size * puzzle.size - 1, index)); setFocus(bounded);
    requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(`[data-grid="main"] [data-cell-index="${bounded}"]`)?.focus());
  };
  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, row: number, col: number) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); moveFocus(row * puzzle.size + Math.min(puzzle.size - 1, col + 1)); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); moveFocus(row * puzzle.size + Math.max(0, col - 1)); }
    if (event.key === 'ArrowDown') { event.preventDefault(); moveFocus(Math.min(puzzle.size - 1, row + 1) * puzzle.size + col); }
    if (event.key === 'ArrowUp') { event.preventDefault(); moveFocus(Math.max(0, row - 1) * puzzle.size + col); }
    if (event.key === 'Home') { event.preventDefault(); moveFocus(row * puzzle.size); }
    if (event.key === 'End') { event.preventDefault(); moveFocus(row * puzzle.size + puzzle.size - 1); }
  };
  return <div className="board-shell">
    <div className="board" role="grid" aria-label={`${puzzle.size} × ${puzzle.size} Shibadoku tábla`} data-grid="main" style={{ '--size': puzzle.size } as React.CSSProperties}>
      {cells.flatMap((line, row) => line.map((state, col) => {
        const region = puzzle.regions[row][col]; const index = row * puzzle.size + col;
        const top = row === 0 || puzzle.regions[row - 1][col] !== region;
        const bottom = row === puzzle.size - 1 || puzzle.regions[row + 1][col] !== region;
        const left = col === 0 || puzzle.regions[row][col - 1] !== region;
        const right = col === puzzle.size - 1 || puzzle.regions[row][col + 1] !== region;
        const given = isGiven(puzzle, row, col);
        return <button key={`${row}-${col}`} role="gridcell" data-cell-index={index} tabIndex={focus === index ? 0 : -1}
          className={`board-cell region-${region % 9} ${patterns ? `pattern-${region % 4}` : ''} ${conflicts.has(`${row}:${col}`) ? 'has-conflict' : ''}`}
          style={{ backgroundColor: regionColors[region % regionColors.length], borderTopWidth: top ? 4 : 1, borderBottomWidth: bottom ? 4 : 1, borderLeftWidth: left ? 4 : 1, borderRightWidth: right ? 4 : 1 }}
          aria-label={`${row + 1}. sor, ${col + 1}. oszlop, ${region + 1}. terület, ${state === 'empty' ? 'üres' : state === 'excluded' ? 'X' : 'Shiba'}${given ? ', zárolt' : ''}`}
          disabled={given} onFocus={() => setFocus(index)} onKeyDown={(event) => keyDown(event, row, col)} onClick={() => onCell(row, col)}>
          {state === 'excluded' && <span className="cell-x" aria-hidden="true">×</span>}
          {state === 'shiba' && <img className="cell-shiba" src={markerAsset} alt="" draggable={false} />}
          {given && <span className="given-dot" aria-hidden="true">●</span>}
          <span className="region-number" aria-hidden="true">{region + 1}</span>
        </button>;
      }))}
    </div>
  </div>;
}
